import React, { useEffect, useMemo, useRef, useState } from "react";
import CampaignBuilder from "../components/campaign/CampaignBuilder";
import MobileScrollArea from "../components/MobileScrollArea";
import { readCache, writeCache } from "../dataCache";

const API_URL = window.location.origin;

const TABS = ["All", "Draft", "Scheduled", "Sending", "Sent", "Archive"];
const DETAIL_TABS = ["Performance", "Overview", "Polls"];

const DATE_PRESETS = [
  { label: "Today", type: "today" },
  { label: "Yesterday", type: "yesterday" },
  { label: "This week", type: "thisWeek" },
  { label: "Last week", type: "lastWeek" },
  { label: "This month", type: "thisMonth" },
  { label: "Last month", type: "lastMonth" },
  { label: "This year", type: "thisYear" },
  { label: "Last year", type: "lastYear" },
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
  { label: "All time", type: "allTime" }
];

const CONTACT_TABS = [
  { label: "All", type: "all" },
  { label: "Sent Emails", type: "sent" },
  { label: "Opened Emails", type: "open" },
  { label: "Clicked Email Links", type: "click" },
  { label: "Unsubscribed", type: "unsubscribe" },
  { label: "Bounced", type: "bounce" }
];

const DEFAULT_COLUMNS = {
  name: true,
  email: true,
  status: true,
  sentTime: true,
  opens: true,
  clicks: true
};

function safeNumber(value) {
  const number = Number(value || 0);
  return Number.isNaN(number) ? 0 : number;
}

function formatNumber(value) {
  return safeNumber(value).toLocaleString();
}

function statusLabel(status) {
  const s = String(status || "draft").toLowerCase();

  if (s === "queued" || s === "sending") return "Sending";
  if (s === "scheduled") return "Scheduled";
  if (s === "sent") return "Sent";
  if (s === "partial") return "Partial";
  if (s === "failed") return "Failed";
  if (s === "archived") return "Archived";

  return "Draft";
}

function isSentCampaign(campaign) {
  return statusLabel(campaign?.status) === "Sent";
}

function safeDate(value) {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date() : date;
}

function formatDate(date) {
  if (!date) return "-";

  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return "-";

  return parsed.toLocaleString();
}

function formatRangeDate(date) {
  return safeDate(date).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
}

function safeHtml(html) {
  return html || "<h1 style='font-family:Arial'>No preview available</h1>";
}

function percent(value, total) {
  const totalNumber = safeNumber(total);
  if (!totalNumber) return "0.00%";
  return `${((safeNumber(value) / totalNumber) * 100).toFixed(2)}%`;
}

function createPresetRange(presetOrDays) {
  const now = new Date();
  const start = new Date(now);
  const end = new Date(now);

  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);

  const preset =
    typeof presetOrDays === "number"
      ? { days: presetOrDays }
      : presetOrDays || { days: 30 };

  if (preset.type === "today") {
    return { start, end };
  }

  if (preset.type === "yesterday") {
    start.setDate(start.getDate() - 1);
    end.setDate(end.getDate() - 1);
    return { start, end };
  }

  if (preset.type === "thisWeek") {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day + 1);
    return { start, end };
  }

  if (preset.type === "lastWeek") {
    const day = start.getDay() || 7;
    start.setDate(start.getDate() - day - 6);
    end.setTime(start.getTime());
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "thisMonth") {
    start.setDate(1);
    return { start, end };
  }

  if (preset.type === "lastMonth") {
    start.setDate(1);
    start.setMonth(start.getMonth() - 1);
    end.setDate(0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "thisYear") {
    start.setMonth(0, 1);
    return { start, end };
  }

  if (preset.type === "lastYear") {
    start.setFullYear(start.getFullYear() - 1, 0, 1);
    end.setFullYear(end.getFullYear() - 1, 11, 31);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }

  if (preset.type === "allTime") {
    start.setFullYear(2000, 0, 1);
    return { start, end };
  }

  const days = Number(preset.days || 30);
  start.setDate(end.getDate() - days + 1);
  return { start, end };
}

function isSameDay(a, b) {
  if (!a || !b) return false;

  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isBetween(date, start, end) {
  if (!date || !start || !end) return false;

  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const s = new Date(start.getFullYear(), start.getMonth(), start.getDate()).getTime();
  const e = new Date(end.getFullYear(), end.getMonth(), end.getDate()).getTime();

  return d >= s && d <= e;
}

function buildCalendarDays(monthDate) {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();

  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const startOffset = firstDay.getDay();
  const totalDays = lastDay.getDate();

  const days = [];

  for (let i = 0; i < startOffset; i++) {
    days.push(null);
  }

  for (let day = 1; day <= totalDays; day++) {
    days.push(new Date(year, month, day));
  }

  return days;
}

function downloadCsv(rows, fileName) {
  const csv = rows
    .map((row) =>
      row.map((value) => `"${String(value || "").replaceAll('"', '""')}"`).join(",")
    )
    .join("\n");

  const blob = new Blob([csv], {
    type: "text/csv"
  });

  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");

  a.href = url;
  a.download = fileName;
  a.click();

  URL.revokeObjectURL(url);
}

function getValueByPath(obj, path) {
  return path.split(".").reduce((acc, key) => acc && acc[key], obj);
}

function getFirstValue(obj, paths) {
  for (const path of paths) {
    const value = getValueByPath(obj, path);

    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }

  return null;
}

function getArrayValue(obj, paths) {
  for (const path of paths) {
    const value = getValueByPath(obj, path);

    if (Array.isArray(value)) {
      return value;
    }
  }

  return [];
}

function getMetricValue(obj, paths) {
  const value = getFirstValue(obj, paths);

  if (Array.isArray(value)) {
    return value.length;
  }

  if (value !== null) {
    return safeNumber(value);
  }

  return 0;
}

function getSentCount(campaign, performance) {
  return (
    getMetricValue(performance?.totals || {}, ["sent", "sentCount", "emailsSent"]) ||
    getMetricValue(campaign, [
      "sent",
      "sentCount",
      "emailsSent",
      "totalSent",
      "totalRecipients",
      "recipientCount",
      "recipientsCount",
      "stats.sent",
      "metrics.sent",
      "analytics.sent",
      "report.sent",
      "recipients",
      "selectedContacts",
      "sentContacts",
      "sentEmails"
    ])
  );
}

function getOpenedCount(campaign, performance) {
  const direct =
    getMetricValue(performance?.totals || {}, [
      "opened",
      "open",
      "opens",
      "openedCount",
      "emailsOpened"
    ]) ||
    getMetricValue(campaign, [
      "opened",
      "open",
      "opens",
      "openCount",
      "openedCount",
      "emailsOpened",
      "totalOpens",
      "stats.opened",
      "stats.opens",
      "metrics.opened",
      "metrics.opens",
      "analytics.opened",
      "analytics.opens",
      "report.opened",
      "report.opens",
      "openedContacts",
      "openedEmails",
      "openEvents"
    ]);

  if (direct) return direct;

  const sent = getSentCount(campaign, performance);

  const rate =
    performance?.totals?.openRate ||
    campaign?.openRate ||
    campaign?.stats?.openRate ||
    campaign?.metrics?.openRate ||
    campaign?.analytics?.openRate;

  if (rate !== undefined && rate !== null) {
    return Math.round((sent * safeNumber(rate)) / 100);
  }

  return 0;
}

function getClickedCount(campaign, performance) {
  const direct =
    getMetricValue(performance?.totals || {}, [
      "clicked",
      "click",
      "clicks",
      "clickedCount",
      "linksClicked"
    ]) ||
    getMetricValue(campaign, [
      "clicked",
      "click",
      "clicks",
      "clickCount",
      "clickedCount",
      "linksClicked",
      "emailsClicked",
      "totalClicks",
      "stats.clicked",
      "stats.clicks",
      "stats.linksClicked",
      "metrics.clicked",
      "metrics.clicks",
      "metrics.linksClicked",
      "analytics.clicked",
      "analytics.clicks",
      "analytics.linksClicked",
      "report.clicked",
      "report.clicks",
      "report.linksClicked",
      "clickedContacts",
      "clickedEmails",
      "clickEvents"
    ]);

  if (direct) return direct;

  const sent = getSentCount(campaign, performance);

  const rate =
    performance?.totals?.clickRate ||
    campaign?.clickRate ||
    campaign?.stats?.clickRate ||
    campaign?.metrics?.clickRate ||
    campaign?.analytics?.clickRate;

  if (rate !== undefined && rate !== null) {
    return Math.round((sent * safeNumber(rate)) / 100);
  }

  return 0;
}

function uniqueByEmail(items) {
  const emails = new Set();

  items.forEach((item) => {
    if (typeof item === "string") {
      emails.add(item.toLowerCase());
      return;
    }

    const email = item?.email || item?.recipientEmail || item?.contactEmail;

    if (email) {
      emails.add(String(email).toLowerCase());
    }
  });

  return emails.size;
}

function getUniqueOpens(campaign, performance) {
  const direct =
    getMetricValue(performance?.totals || {}, [
      "uniqueOpens",
      "uniqueOpened",
      "uniqueOpenCount"
    ]) ||
    getMetricValue(campaign, [
      "uniqueOpens",
      "uniqueOpened",
      "uniqueOpenCount",
      "stats.uniqueOpens",
      "metrics.uniqueOpens",
      "analytics.uniqueOpens",
      "report.uniqueOpens"
    ]);

  if (direct) return direct;

  const arrays = [
    ...getArrayValue(campaign, ["openedContacts"]),
    ...getArrayValue(campaign, ["openedEmails"]),
    ...getArrayValue(campaign, ["openEvents"]),
    ...getArrayValue(campaign, ["stats.openEvents"]),
    ...getArrayValue(campaign, ["analytics.openEvents"]),
    ...getArrayValue(campaign, ["report.openEvents"]),
    ...getArrayValue(performance || {}, ["openEvents"]),
    ...getArrayValue(performance || {}, ["openedContacts"])
  ];

  if (arrays.length) {
    return uniqueByEmail(arrays);
  }

  return getOpenedCount(campaign, performance);
}

function getUniqueClicks(campaign, performance) {
  const direct =
    getMetricValue(performance?.totals || {}, [
      "uniqueClicks",
      "uniqueClicked",
      "uniqueClickCount"
    ]) ||
    getMetricValue(campaign, [
      "uniqueClicks",
      "uniqueClicked",
      "uniqueClickCount",
      "stats.uniqueClicks",
      "metrics.uniqueClicks",
      "analytics.uniqueClicks",
      "report.uniqueClicks"
    ]);

  if (direct) return direct;

  const arrays = [
    ...getArrayValue(campaign, ["clickedContacts"]),
    ...getArrayValue(campaign, ["clickedEmails"]),
    ...getArrayValue(campaign, ["clickEvents"]),
    ...getArrayValue(campaign, ["stats.clickEvents"]),
    ...getArrayValue(campaign, ["analytics.clickEvents"]),
    ...getArrayValue(campaign, ["report.clickEvents"]),
    ...getArrayValue(performance || {}, ["clickEvents"]),
    ...getArrayValue(performance || {}, ["clickedContacts"])
  ];

  if (arrays.length) {
    return uniqueByEmail(arrays);
  }

  return getClickedCount(campaign, performance);
}

function getUnsubscribedCount(campaign, performance) {
  return (
    getMetricValue(performance?.totals || {}, [
      "unsubscribed",
      "unsubscribeCount",
      "unsubscribers"
    ]) ||
    getMetricValue(campaign, [
      "unsubscribed",
      "unsubscribedCount",
      "unsubscribeCount",
      "unsubscribers",
      "stats.unsubscribed",
      "metrics.unsubscribed",
      "analytics.unsubscribed",
      "report.unsubscribed",
      "unsubscribedContacts"
    ])
  );
}

function getBouncedCount(campaign, performance) {
  return (
    getMetricValue(performance?.totals || {}, ["bounced", "bounceCount", "bounces"]) ||
    getMetricValue(campaign, [
      "bounced",
      "bouncedCount",
      "bounceCount",
      "bounces",
      "stats.bounced",
      "metrics.bounced",
      "analytics.bounced",
      "report.bounced",
      "bouncedContacts"
    ])
  );
}

function buildTotals(campaign, performance) {
  // When a date-filtered performance response is present, EVERY number must
  // come only from performance.totals for the selected range. Never fall back
  // to the campaign's stored all-time counts, otherwise a date with no data
  // would still show the campaign totals in the boxes.
  if (performance && performance.totals) {
    const t = performance.totals;
    const num = (v) => Number(v) || 0;
    const sent = num(t.sent);
    const opened = num(t.opened);
    const clicked = num(t.clicked);
    const uniqueOpens = num(t.uniqueOpens);
    const uniqueClicks = num(t.uniqueClicks);
    const unsubscribed = num(t.unsubscribed);
    const bounced = num(t.bounced);
    return {
      sent,
      opened,
      clicked,
      uniqueOpens,
      uniqueClicks,
      unsubscribed,
      bounced,
      // Total boxes -> rate over ALL opens/clicks (repeats included).
      // Unique boxes -> rate over one-per-client opens/clicks.
      openRate: percent(opened, sent),
      clickRate: percent(clicked, sent),
      uniqueOpenRate: percent(uniqueOpens, sent),
      uniqueClickRate: percent(uniqueClicks, sent)
    };
  }

  const sent = getSentCount(campaign, performance);
  const opened = getOpenedCount(campaign, performance);
  const clicked = getClickedCount(campaign, performance);
  const uniqueOpens = getUniqueOpens(campaign, performance);
  const uniqueClicks = getUniqueClicks(campaign, performance);
  const unsubscribed = getUnsubscribedCount(campaign, performance);
  const bounced = getBouncedCount(campaign, performance);

  return {
    sent,
    opened,
    clicked,
    uniqueOpens,
    uniqueClicks,
    unsubscribed,
    bounced,
    openRate: percent(opened, sent),
    clickRate: percent(clicked, sent),
    uniqueOpenRate: percent(uniqueOpens, sent),
    uniqueClickRate: percent(uniqueClicks, sent)
  };
}

function getSentTime(campaign) {
  return (
    campaign?.sentAt ||
    campaign?.sentDate ||
    campaign?.completedAt ||
    campaign?.scheduledAt ||
    campaign?.createdAt ||
    campaign?.date
  );
}

// Status-aware date shown next to a campaign:
// - scheduled  -> the time it is scheduled to go out (scheduledAt), NOT creation time
// - sent/partial -> the actual time it was sent (sentAt)
// - otherwise  -> creation time
function campaignWhen(campaign) {
  const s = String(campaign?.status || "").toLowerCase();
  if (s === "scheduled") {
    return campaign?.scheduledAt || campaign?.scheduledDate || campaign?.createdAt;
  }
  if (s === "sent" || s === "partial") {
    return campaign?.sentAt || campaign?.completedAt || campaign?.scheduledAt || campaign?.createdAt;
  }
  if (s === "sending" || s === "queued") {
    return campaign?.sentAt || campaign?.scheduledAt || campaign?.createdAt;
  }
  return campaign?.createdAt;
}

function getContactName(contact) {
  if (typeof contact === "string") return "-";

  return (
    contact?.name ||
    `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim() ||
    contact?.email ||
    "-"
  );
}

function getContactEmail(contact) {
  if (typeof contact === "string") return contact;

  return contact?.email || contact?.recipientEmail || contact?.contactEmail || "-";
}

function normalizeContact(contact, fallbackStatus, campaign) {
  const email = getContactEmail(contact);

  return {
    id: contact?._id || contact?.id || email,
    senderName: contact?.senderName || contact?.fromName || "",
    senderEmail: contact?.senderEmail || contact?.fromEmail || "",
    name: getContactName(contact),
    email,
    status: contact?.status || fallbackStatus,
    sentTime:
      contact?.sentTime ||
      contact?.sentAt ||
      contact?.createdAt ||
      getSentTime(campaign),
    opens: safeNumber(contact?.opens || contact?.openCount || contact?.opened || 0),
    clicks: safeNumber(contact?.clicks || contact?.clickCount || contact?.clicked || 0),
    error: contact?.error || "",
    device: contact?.device || "",
    platform: contact?.platform || "",
    client: contact?.client || "",
    country: contact?.country || ""
  };
}

function buildFallbackRows(campaign) {
  const sentContacts = getArrayValue(campaign, [
    "sentContacts",
    "recipients",
    "selectedContacts",
    "contacts",
    "sentEmails"
  ]);

  const openedContacts = getArrayValue(campaign, [
    "openedContacts",
    "openedEmails",
    "openEvents",
    "stats.openEvents",
    "analytics.openEvents",
    "report.openEvents"
  ]);

  const clickedContacts = getArrayValue(campaign, [
    "clickedContacts",
    "clickedEmails",
    "clickEvents",
    "stats.clickEvents",
    "analytics.clickEvents",
    "report.clickEvents"
  ]);

  const unsubscribedContacts = getArrayValue(campaign, [
    "unsubscribedContacts",
    "unsubscribeEvents",
    "stats.unsubscribeEvents",
    "analytics.unsubscribeEvents",
    "report.unsubscribeEvents"
  ]);

  const bouncedContacts = getArrayValue(campaign, [
    "bouncedContacts",
    "bounceEvents",
    "stats.bounceEvents",
    "analytics.bounceEvents",
    "report.bounceEvents"
  ]);

  const map = new Map();

  sentContacts.forEach((contact) => {
    const row = normalizeContact(contact, "sent", campaign);
    map.set(row.email, row);
  });

  openedContacts.forEach((contact) => {
    const row = normalizeContact(contact, "opened", campaign);
    const old = map.get(row.email) || row;

    map.set(row.email, {
      ...old,
      status: old.status === "clicked" ? "clicked" : "opened",
      opens: Math.max(old.opens || 0, row.opens || 1)
    });
  });

  clickedContacts.forEach((contact) => {
    const row = normalizeContact(contact, "clicked", campaign);
    const old = map.get(row.email) || row;

    map.set(row.email, {
      ...old,
      status: "clicked",
      clicks: Math.max(old.clicks || 0, row.clicks || 1)
    });
  });

  unsubscribedContacts.forEach((contact) => {
    const row = normalizeContact(contact, "unsubscribed", campaign);
    const old = map.get(row.email) || row;

    map.set(row.email, {
      ...old,
      status: "unsubscribed"
    });
  });

  bouncedContacts.forEach((contact) => {
    const row = normalizeContact(contact, "bounced", campaign);
    const old = map.get(row.email) || row;

    map.set(row.email, {
      ...old,
      status: "bounced"
    });
  });

  return Array.from(map.values());
}

function filterRowsByTab(rows, contactTab) {
  if (contactTab === "all") return rows;

  if (contactTab === "sent") {
    return rows.filter((row) =>
      ["sent", "opened", "clicked", "unsubscribed", "bounced"].includes(row.status)
    );
  }

  if (contactTab === "open") {
    return rows.filter(
      (row) => row.status === "opened" || row.status === "clicked" || row.opens > 0
    );
  }

  if (contactTab === "click") {
    return rows.filter((row) => row.status === "clicked" || row.clicks > 0);
  }

  if (contactTab === "unsubscribe") {
    return rows.filter((row) => row.status === "unsubscribed");
  }

  if (contactTab === "bounce") {
    return rows.filter((row) => row.status === "bounced");
  }

  return rows;
}

function getVisibleColumnsForTab(contactTab, columns) {
  if (contactTab === "sent") {
    return {
      name: columns.name,
      email: columns.email,
      status: false,
      opens: false,
      clicks: false,
      sentTime: columns.sentTime
    };
  }

  if (contactTab === "all") {
    return {
      name: columns.name,
      email: columns.email,
      status: columns.status,
      opens: columns.opens,
      clicks: columns.clicks,
      sentTime: columns.sentTime
    };
  }

  if (contactTab === "open") {
    return {
      name: columns.name,
      email: columns.email,
      status: false,
      opens: columns.opens,
      clicks: false,
      sentTime: columns.sentTime
    };
  }

  if (contactTab === "click") {
    return {
      name: columns.name,
      email: columns.email,
      status: false,
      opens: false,
      clicks: columns.clicks,
      sentTime: columns.sentTime
    };
  }

  return {
    name: columns.name,
    email: columns.email,
    status: columns.status,
    opens: false,
    clicks: false,
    sentTime: columns.sentTime
  };
}

function CalendarMonth({ monthDate, range, onDayClick }) {
  const days = buildCalendarDays(monthDate);

  return (
    <div className="sx-calendar-month">
      <div className="sx-calendar-month-title">
        {monthDate.toLocaleDateString("en-US", {
          month: "long",
          year: "numeric"
        })}
      </div>

      <div className="sx-calendar-weekdays">
        {["S", "M", "T", "W", "T", "F", "S"].map((day, index) => (
          <span key={`${day}-${index}`}>{day}</span>
        ))}
      </div>

      <div className="sx-calendar-days">
        {days.map((day, index) => {
          if (!day) return <span key={`blank-${index}`}></span>;

          const selected =
            isSameDay(day, range.start) ||
            isSameDay(day, range.end) ||
            isBetween(day, range.start, range.end);

          return (
            <button
              type="button"
              key={day.toISOString()}
              className={selected ? "selected" : ""}
              onClick={() => onDayClick(day)}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DateRangePicker({
  selectedPreset,
  customRange,
  activeRange,
  onPresetChange,
  onCustomChange,
  onClose
}) {
  const [viewMonth, setViewMonth] = useState(() => {
    const d = new Date(activeRange.end);
    d.setDate(1);
    return d;
  });

  useEffect(() => {
    const d = new Date(activeRange.end);
    d.setDate(1);
    setViewMonth(d);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPreset]);

  useEffect(() => {
    function handleOutside(e) {
      if (e.target.closest && e.target.closest(".performance-calendar-wrap")) return;
      onClose();
    }
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [onClose]);

  const endMonth = new Date(viewMonth);
  endMonth.setDate(1);

  const startMonth = new Date(endMonth);
  startMonth.setMonth(endMonth.getMonth() - 1);

  function handleDayClick(day) {
    const clickedStart = new Date(day);
    clickedStart.setHours(0, 0, 0, 0);

    const clickedEnd = new Date(day);
    clickedEnd.setHours(23, 59, 59, 999);

    if (!customRange?.start || customRange?.end) {
      onCustomChange({
        start: clickedStart,
        end: null
      });
      return;
    }

    if (clickedEnd < customRange.start) {
      onCustomChange({
        start: clickedStart,
        end: customRange.start
      });
    } else {
      onCustomChange({
        start: customRange.start,
        end: clickedEnd
      });
    }
  }

  const visibleRange = customRange?.start
    ? {
        start: customRange.start,
        end: customRange.end || customRange.start
      }
    : activeRange;

  return (
    <div className="sx-date-picker-panel performance-date-picker">
      <div className="sx-date-presets">
        {DATE_PRESETS.map((preset) => (
          <button
            type="button"
            key={preset.label}
            className={selectedPreset === preset.label && !customRange ? "active" : ""}
            onClick={() => onPresetChange(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>

      <div className="sx-date-calendars">
        <div className="sx-calendar-actions">
          <button type="button" onClick={() => setViewMonth((m) => { const n = new Date(m); n.setMonth(m.getMonth() - 1); return n; })}>‹</button>
          <button type="button" onClick={() => setViewMonth((m) => { const n = new Date(m); n.setMonth(m.getMonth() + 1); return n; })}>›</button>
        </div>

        <div className="sx-calendar-grid">
          <CalendarMonth
            monthDate={startMonth}
            range={visibleRange}
            onDayClick={handleDayClick}
          />

          <CalendarMonth
            monthDate={endMonth}
            range={visibleRange}
            onDayClick={handleDayClick}
          />
        </div>

        <div className="sx-calendar-footer">
          <span>
            {formatRangeDate(visibleRange.start)} - {formatRangeDate(visibleRange.end)}
          </span>

          <button type="button" onClick={onClose}>
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}

function EmailPreviewFrame({ html, className }) {
  const cls = className || "";
  // Small boxes (list thumbnail + content mini) render the email at its full
  // natural width (600px) and are scaled down with JS so the COMPLETE template
  // fits inside the box (contain-fit), even if it ends up small.
  const scaled = cls.includes("thumb") || cls.includes("mini");
  const wrapRef = useRef(null);
  const frameRef = useRef(null);

  const fit = () => {
    const wrap = wrapRef.current;
    const frame = frameRef.current;
    if (!wrap || !frame) return;
    let doc;
    try {
      doc = frame.contentDocument || (frame.contentWindow && frame.contentWindow.document);
    } catch (e) {
      return;
    }
    if (!doc || !doc.body) return;
    // Inline !important beats every author stylesheet rule (incl. .nl-thumb iframe).
    const setI = (prop, val) => frame.style.setProperty(prop, val, "important");
    const VW = 600;
    setI("width", VW + "px");
    setI("transform", "none");
    const ch = Math.max(
      doc.documentElement ? doc.documentElement.scrollHeight : 0,
      doc.body ? doc.body.scrollHeight : 0,
      1
    );
    setI("height", ch + "px");
    const boxW = wrap.clientWidth || 1;
    // Fill the box WIDTH with the full-width (600px) template and top-align it,
    // so the template properly fills the box instead of sitting tiny in a corner.
    const scale = boxW / VW;
    setI("transform", "scale(" + scale + ")");
    setI("transform-origin", "top left");
    setI("left", "0px");
    setI("top", "0px");
  };

  useEffect(() => {
    if (!scaled) return;
    fit();
    const t1 = setTimeout(fit, 120);
    const t2 = setTimeout(fit, 450);
    window.addEventListener("resize", fit);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("resize", fit);
    };
  }, [scaled, html]);

  if (scaled) {
    return (
      <div ref={wrapRef} className={`epf-scaler ${cls}`}>
        <iframe
          ref={frameRef}
          title="Email preview"
          className="epf-scaled-inner"
          scrolling="no"
          srcDoc={safeHtml(html)}
          onLoad={fit}
        />
      </div>
    );
  }
  return (
    <iframe
      title="Email preview"
      className={cls}
      srcDoc={safeHtml(html)}
    />
  );
}

function CampaignSendersList({ campaign }) {
  // Merge every sender source so ALL senders used/selected for the campaign
  // are shown (deduped by email), not just the first resolved SMTP sender.
  const raw = [
    ...(Array.isArray(campaign?.selectedSenders) ? campaign.selectedSenders : []),
    ...(Array.isArray(campaign?.usedSenders) ? campaign.usedSenders : []),
    ...(Array.isArray(campaign?.senders) ? campaign.senders : [])
  ];
  const seen = new Set();
  const senders = raw
    .map((sender) => {
      if (!sender || typeof sender === "string") return null;
      return {
        name: sender.name || sender.senderName || sender.fromName || "Sender",
        email: sender.email || sender.senderEmail || sender.fromEmail || sender.user || ""
      };
    })
    .filter((sender) => {
      if (!sender || !sender.email) return false;
      const key = sender.email.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

  const finalSenders = senders.length
    ? senders
    : [
        {
          name: campaign?.fromName || "Demo",
          email: campaign?.fromEmail || "demo@tryoutsendx.com"
        }
      ];

  return (
    <div className="campaign-senders-list">
      {finalSenders.map((sender, index) => (
        <span className="campaign-sender-pill" key={`${sender.email}-${index}`}>
          {sender.name} &lt;{sender.email}&gt;
        </span>
      ))}
    </div>
  );
}

export default function NewsletterPage() {
  const [tab, setTab] = useState("All");
  const [campaigns, setCampaigns] = useState(() => readCache("campaigns", []));
  const [search, setSearch] = useState("");

  const [builder, setBuilder] = useState(false);
  const [builderCampaign, setBuilderCampaign] = useState(null);

  const [openMenu, setOpenMenu] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailTab, setDetailTab] = useState("Performance");

  const [toast, setToast] = useState("");

  const fmtISO = (d) => {
    const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 10);
  };
  const _perfInitEnd = new Date();
  const _perfInitStart = new Date();
  const [rangeStart, setRangeStart] = useState(fmtISO(_perfInitStart));
  const [rangeEnd, setRangeEnd] = useState(fmtISO(_perfInitEnd));
  const [activePreset, setActivePreset] = useState("Today");

  const [performance, setPerformance] = useState(null);
  const [contactTab, setContactTab] = useState("all");
  const [contactRows, setContactRows] = useState([]);

  const [polls, setPolls] = useState([]);

  const [columnModal, setColumnModal] = useState(false);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  const [selectedIds, setSelectedIds] = useState([]);
  const [listColModal, setListColModal] = useState(false);
  const [listColumns, setListColumns] = useState({
    sent: true,
    opens: true,
    opened: true,
    clicks: true,
    clicked: true,
    created: false
  });
  const [perPage, setPerPage] = useState(25);
  const [pageNum, setPageNum] = useState(1);

  const [loading, setLoading] = useState(false);

  // Newsletters LIST date range (independent of the detail Performance range).
  // Default "All" => show each campaign's lifetime stats with no extra fetches
  // (keeps the list instant). Picking a specific range fetches date-scoped
  // performance for the visible rows only.
  const _listEnd = new Date();
  const _listStart = new Date();
  _listStart.setDate(1);
  const [listRangeStart, setListRangeStart] = useState(fmtISO(_listStart));
  const [listRangeEnd, setListRangeEnd] = useState(fmtISO(_listEnd));
  const [listActivePreset, setListActivePreset] = useState("This month");
  const [listPerfMap, setListPerfMap] = useState({});
  const LIST_PRESETS = ["Today", "Yesterday", "This month", "Last month", "All"];

  function applyListPreset(label) {
    const e = new Date();
    let s = new Date();
    if (label === "Today") { /* both today */ }
    else if (label === "Yesterday") { s.setDate(s.getDate() - 1); e.setDate(e.getDate() - 1); }
    else if (label === "This month") { s.setDate(1); }
    else if (label === "Last month") { s.setMonth(s.getMonth() - 1, 1); e.setDate(0); }
    else if (label === "All") { s = new Date(2000, 0, 1); }
    setListRangeStart(fmtISO(s));
    setListRangeEnd(fmtISO(e));
    setListActivePreset(label);
  }

  const contactsScrollRef = useRef(null);
  function scrollContacts(dir) {
    const el = contactsScrollRef.current;
    if (el) el.scrollBy({ left: dir * 320, behavior: "smooth" });
  }

  const activeRange = {
    start: new Date(rangeStart + "T00:00:00"),
    end: new Date(rangeEnd + "T23:59:59")
  };

  const PERF_PRESETS = ["Today", "Yesterday", "This month", "Last month", "All"];

  function applyPreset(label) {
    const e = new Date();
    let s = new Date();
    if (label === "Today") { /* both today */ }
    else if (label === "Yesterday") { s.setDate(s.getDate() - 1); e.setDate(e.getDate() - 1); }
    else if (label === "This month") { s.setDate(1); }
    else if (label === "Last month") { s.setMonth(s.getMonth() - 1, 1); e.setDate(0); }
    else if (label === "All") { s = new Date(2000, 0, 1); }
    setRangeStart(fmtISO(s));
    setRangeEnd(fmtISO(e));
    setActivePreset(label);
  }

  const dateRangeText = `${formatRangeDate(activeRange.start)} - ${formatRangeDate(
    activeRange.end
  )}`;

  function syncHashForDetail(campaignId, nextTab = detailTab, nextContactTab) {
    if (!campaignId) return;
    const tabSlug = String(nextTab || "Performance").toLowerCase();
    let nextHash = `#/Newsletters/campaign/${campaignId}/${tabSlug}`;
    if (tabSlug === "performance" && nextContactTab && nextContactTab !== "all") {
      nextHash += `/${nextContactTab}`;
    }
    if (window.location.hash !== nextHash) {
      window.location.hash = nextHash;
    }
  }

  function getTabFromSlug(slug) {
    const value = String(slug || "performance").toLowerCase();
    if (value === "overview") return "Overview";
    if (value === "polls") return "Polls";
    return "Performance";
  }

  function getContactTabFromSlug(slug) {
    const value = String(slug || "").toLowerCase();
    if (["sent", "open", "click", "unsubscribe", "bounce", "all"].includes(value)) {
      return value;
    }
    return "all";
  }

  function showToast(message) {
    setToast(message);
    setTimeout(() => setToast(""), 2600);
  }

  // Remove deleted campaigns from the Dashboard cache immediately so the
  // Dashboard never shows a stale "Emails sent" total that still counts them.
  function pruneDashCache(ids) {
    const idSet = new Set((ids || []).map(String));
    const remaining = (readCache("dash_campaigns", []) || []).filter(
      (c) => !idSet.has(String(c._id || c.id))
    );
    writeCache("dash_campaigns", remaining);
  }

  async function loadCampaigns() {
    try {
      const res = await fetch(`${API_URL}/api/campaigns`);
      const data = await res.json();

      const list = Array.isArray(data)
        ? data
        : data.campaigns || data.data || data.items || [];

      setCampaigns(list);
      writeCache("campaigns", list);
    } catch (err) {
      console.log("Campaign load error:", err.message);
      setCampaigns([]);
    }
  }

  async function loadPerformance(campaignId, s = rangeStart, e = rangeEnd) {
    const cacheKey = "perf_" + campaignId + "_" + s + "_" + e;
    const cached = readCache(cacheKey, null);
    if (cached) setPerformance(cached);
    try {
      if (!cached) setLoading(true);

      const res = await fetch(
        `${API_URL}/api/campaigns/${campaignId}/performance?start=${encodeURIComponent(
          s
        )}&end=${encodeURIComponent(e)}`
      );

      const data = await res.json();
      setPerformance(data);
      writeCache(cacheKey, data);
    } catch (err) {
      console.log("Performance load error:", err.message);
      if (!cached) setPerformance(null);
    } finally {
      setLoading(false);
    }
  }

  async function loadContacts(campaignId, type = contactTab, s = rangeStart, e = rangeEnd) {
    const cacheKey = "contacts_" + campaignId + "_" + type + "_" + s + "_" + e;
    const cached = readCache(cacheKey, null);
    if (cached) setContactRows(Array.isArray(cached) ? cached : []);
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/contacts?type=${type}&start=${encodeURIComponent(s)}&end=${encodeURIComponent(e)}`);
      const data = await res.json();
      const rows = Array.isArray(data) ? data : data.contacts || data.data || data.items || [];
      const nextRows = Array.isArray(rows) ? rows : [];
      setContactRows(nextRows);
      writeCache(cacheKey, nextRows);
    } catch (err) {
      console.log("Contacts load error:", err.message);
      if (!cached) setContactRows([]);
    }
  }


  async function loadPolls(campaignId) {
    try {
      const res = await fetch(`${API_URL}/api/campaigns/${campaignId}/polls`);
      const data = await res.json();

      setPolls(Array.isArray(data) ? data : data.polls || []);
    } catch {
      setPolls([]);
    }
  }

  useEffect(() => {
    loadCampaigns();
    const timer = setInterval(loadCampaigns, 8000);
    return () => clearInterval(timer);
  }, []);

  // Effect A: OPEN a campaign detail when the hash points at one. This runs on
  // mount, on every hashchange, and whenever `campaigns` reloads (so a detail
  // opened via a direct URL resolves once its campaign has loaded). It ONLY
  // opens details — it never closes them — so the 8s campaign refresh can never
  // navigate the user away by itself.
  useEffect(() => {
    function openDetailFromHash() {
      const raw = (window.location.hash || "").replace(/^#\/?/, "");
      const parts = raw.split("/").map(decodeURIComponent);
      if (parts[0] !== "Newsletters" || parts[1] !== "campaign" || !parts[2]) return;
      const found = campaigns.find((campaign) => String(campaign._id || campaign.id) === String(parts[2]));
      if (!found) return;
      setDetails(found);
      const nextDetailTab = getTabFromSlug(parts[3]);
      setDetailTab(nextDetailTab);
      setContactTab(nextDetailTab === "Performance" ? getContactTabFromSlug(parts[4]) : "all");
      setOpenMenu(null);
    }

    openDetailFromHash();
    window.addEventListener("hashchange", openDetailFromHash);
    return () => window.removeEventListener("hashchange", openDetailFromHash);
  }, [campaigns]);

  // Effect B: navigation transitions that should happen ONLY in response to a
  // real user navigation (a hashchange), never on the 8s refresh or on mount:
  //   - #/Newsletters/new  -> open the campaign builder (e.g. Dashboard button)
  //   - #/Newsletters      -> go back to the list, closing any open detail/builder
  // Because this listener is not called on mount and does not depend on
  // `campaigns`, the list/builder can only open or close from an explicit click.
  const skipNextBaseClear = useRef(false);
  // True while the builder was opened from the Dashboard "Create Newsletter"
  // button, so that after a successful SEND we return to the Dashboard.
  const builderFromDashboard = useRef(false);
  useEffect(() => {
    function onNavigate() {
      const raw = (window.location.hash || "").replace(/^#\/?/, "");
      const parts = raw.split("/").map(decodeURIComponent);
      if (parts[0] === "Newsletters" && parts[1] === "new") {
        // Opening the builder then normalising the hash back to base would fire
        // this listener again; flag it so the base branch does not immediately
        // close the builder we just opened.
        skipNextBaseClear.current = true;
        builderFromDashboard.current = true;
        setBuilder(true);
        window.location.hash = "#/Newsletters";
        return;
      }
      if (parts[0] === "Newsletters" && !parts[1]) {
        if (skipNextBaseClear.current) {
          skipNextBaseClear.current = false;
          return;
        }
        setDetails(null);
        setBuilder(false);
      }
    }

    window.addEventListener("hashchange", onNavigate);
    return () => window.removeEventListener("hashchange", onNavigate);
  }, []);

  useEffect(() => {
    if (!details?._id) return;

    if (detailTab === "Performance") {
      loadPerformance(details._id, rangeStart, rangeEnd);
    }

    if (detailTab === "Polls") {
      loadPolls(details._id);
    }
  }, [details?._id, detailTab, rangeStart, rangeEnd]);

  useEffect(() => {
    if (!details?._id) return;
    if (detailTab === "Performance") {
      loadContacts(details._id, contactTab, rangeStart, rangeEnd);
    }
  }, [details?._id, detailTab, contactTab, rangeStart, rangeEnd]);

  // REQ-25: Live auto-refresh of the Performance tab. Without this, opens/clicks
  // only loaded once when the tab opened, so new opens/clicks looked "stuck".
  // Now while viewing Performance we silently re-fetch metrics + contact rows
  // every 4s so Opens, Unique Opens, Clicks and Unique Clicks update in near
  // real-time (mobile + PC), exactly matching what the backend has recorded.
  useEffect(() => {
    if (!details?._id || detailTab !== "Performance") return;
    const id = details._id;
    const timer = setInterval(() => {
      // Skip refresh when the tab is hidden to save resources; resume on focus.
      if (typeof document !== "undefined" && document.hidden) return;
      loadPerformance(id, rangeStart, rangeEnd);
      loadContacts(id, contactTab, rangeStart, rangeEnd);
    }, 4000);
    return () => clearInterval(timer);
  }, [details?._id, detailTab, contactTab, rangeStart, rangeEnd]);


  useEffect(() => {
    if (!details?._id) return;
    const fresh = campaigns.find((campaign) => String(campaign._id || campaign.id) === String(details._id || details.id));
    if (fresh && JSON.stringify(fresh) !== JSON.stringify(details)) {
      setDetails(fresh);
    }
  }, [campaigns, details?._id]);

  const filtered = useMemo(() => {
    return campaigns.filter((campaign) => {
      const label = statusLabel(campaign.status);
      const isArchived = label.toLowerCase() === "archived";
      // Archived campaigns show ONLY in the dedicated "Archive" tab and are
      // automatically hidden from All / Draft / Scheduled / Sending / Sent.
      let matchTab;
      if (tab === "Archive") matchTab = isArchived;
      else if (tab === "All") matchTab = !isArchived;
      else matchTab = !isArchived && label.toLowerCase() === tab.toLowerCase();

      const q = search.trim().toLowerCase();

      const matchSearch =
        !q ||
        String(campaign.name || "").toLowerCase().includes(q) ||
        String(campaign.subject || "").toLowerCase().includes(q);

      return matchTab && matchSearch;
    });
  }, [campaigns, tab, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const safePage = Math.min(pageNum, totalPages);
  const paged = filtered.slice((safePage - 1) * perPage, safePage * perPage);
  const allSelected = paged.length > 0 && paged.every((c) => selectedIds.includes(c._id));

  // When a specific list date range is active, fetch date-scoped performance
  // for just the visible (paged) campaigns. "All" uses lifetime stats and needs
  // no fetch, so the default list stays instant.
  const pagedIdsKey = paged.map((c) => c._id).join(",");
  useEffect(() => {
    if (listActivePreset === "All") {
      setListPerfMap({});
      return;
    }
    const ids = paged.map((c) => c._id).filter(Boolean);
    if (ids.length === 0) {
      setListPerfMap({});
      return;
    }
    let cancelled = false;
    fetch(
      `${API_URL}/api/campaign-performance-batch?ids=${encodeURIComponent(
        ids.join(",")
      )}&start=${encodeURIComponent(listRangeStart)}&end=${encodeURIComponent(listRangeEnd)}`
    )
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setListPerfMap((data && data.map) || {});
      })
      .catch(() => {
        if (!cancelled) setListPerfMap({});
      });
    return () => {
      cancelled = true;
    };
  }, [pagedIdsKey, listRangeStart, listRangeEnd, listActivePreset]);

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds((prev) => prev.filter((id) => !paged.some((c) => c._id === id)));
    } else {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...paged.map((c) => c._id)])));
    }
  }

  function toggleSelectOne(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  async function deleteSelected() {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} selected campaign(s)?`)) return;
    await Promise.all(
      selectedIds.map((id) =>
        fetch(`${API_URL}/api/campaigns/${id}`, { method: "DELETE" })
      )
    );
    pruneDashCache(selectedIds);
    setSelectedIds([]);
    showToast("Selected campaigns deleted successfully.");
    loadCampaigns();
  }

  async function archiveSelected() {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Archive ${selectedIds.length} selected campaign(s)?`)) return;
    await Promise.all(
      selectedIds.map((id) =>
        fetch(`${API_URL}/api/campaigns/${id}/archive`, { method: "PATCH" })
      )
    );
    pruneDashCache(selectedIds);
    setSelectedIds([]);
    showToast("Selected campaigns archived successfully.");
    loadCampaigns();
  }

  function openDetail(campaign, nextTab = "Performance") {
    setDetails(campaign);
    setDetailTab(nextTab);
    syncHashForDetail(campaign?._id || campaign?.id, nextTab);
    setContactTab("all");
    setOpenMenu(null);
    setPerformance(null);
    setContactRows([]);

    window.scrollTo({
      top: 0,
      behavior: "smooth"
    });
  }

  async function duplicateCampaign(campaign) {
    await fetch(`${API_URL}/api/campaigns/${campaign._id}/duplicate`, {
      method: "POST"
    });

    setOpenMenu(null);
    showToast("Campaign duplicated successfully.");
    loadCampaigns();
  }

  async function archiveCampaign(campaign) {
    await fetch(`${API_URL}/api/campaigns/${campaign._id}/archive`, {
      method: "PATCH"
    });

    setOpenMenu(null);
    showToast("Campaign archived successfully.");
    loadCampaigns();
  }

  async function deleteCampaign(campaign) {
    if (isSentCampaign(campaign)) {
      showToast("Sent campaigns cannot be deleted.");
      return;
    }

    if (!window.confirm("Delete this campaign?")) return;

    await fetch(`${API_URL}/api/campaigns/${campaign._id}`, {
      method: "DELETE"
    });

    pruneDashCache([campaign._id]);
    setOpenMenu(null);
    showToast("Campaign deleted successfully.");
    loadCampaigns();
  }

  function saveTemplateOnly() {
    showToast("Campaign template saved successfully.");
  }

  async function createPoll() {
    const question = prompt("Poll question");

    if (!question || !details?._id) return;

    await fetch(`${API_URL}/api/campaigns/${details._id}/polls`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        question,
        options: ["Excellent", "Good", "Bad"]
      })
    });

    loadPolls(details._id);
    showToast("Poll created successfully.");
  }

  function exportCampaigns() {
    downloadCsv(
      [
        ["Name", "Subject", "Status", "Sent", "Created"],
        ...filtered.map((campaign) => [
          campaign.name || "",
          campaign.subject || "",
          statusLabel(campaign.status),
          getSentCount(campaign, null),
          formatDate(campaign.createdAt)
        ])
      ],
      "newsletters.csv"
    );
  }

  function exportContacts(rowsToExport) {
    const activeColumns = getVisibleColumnsForTab(contactTab, columns);

    const headers = [];

    if (activeColumns.name) headers.push("Name");
    if (activeColumns.email) headers.push("Email");
    if (activeColumns.status) headers.push("Status");
    if (activeColumns.opens) {
      headers.push(contactTab === "all" ? "Unique Opens" : "Opens");
    }
    if (activeColumns.clicks) {
      headers.push(contactTab === "all" ? "Unique Clicks" : "Clicks");
    }
    if (activeColumns.sentTime) headers.push("Sent Time");

    const rows = rowsToExport.map((row) => {
      const output = [];

      if (activeColumns.name) output.push(row.name || "");
      if (activeColumns.email) output.push(row.email || "");
      if (activeColumns.status) output.push(row.status || "");
      if (activeColumns.opens) output.push(row.opens || 0);
      if (activeColumns.clicks) output.push(row.clicks || 0);
      if (activeColumns.sentTime) output.push(formatDate(row.sentTime));

      return output;
    });

    downloadCsv([headers, ...rows], "campaign-contacts.csv");
  }

  if (builder) {
    return (
      <CampaignBuilder
        initialCampaign={builderCampaign}
        onClose={(result) => {
          setBuilder(false);
          setBuilderCampaign(null);
          loadCampaigns();
          const fromDashboard = builderFromDashboard.current;
          builderFromDashboard.current = false;
          // When the builder was launched from the Dashboard and the campaign
          // was actually sent, take the user back to the Dashboard.
          if (result?.sent && fromDashboard) {
            window.location.hash = "#/Dashboard";
          }
        }}
      />
    );
  }

  if (details) {
    const totals = buildTotals(details, performance);

    const fallbackRows = buildFallbackRows(details);

    const normalizedApiRows = contactRows.map((row) =>
      normalizeContact(
        row,
        contactTab === "all" ? row.status || "sent" : contactTab,
        details
      )
    );

    // Only show rows returned by the (date-filtered) contacts API. Do NOT fall
    // back to campaign-derived rows, so a date with no activity shows "No Data".
    const allRows = normalizedApiRows;
    const visibleRows = filterRowsByTab(allRows, contactTab);
    const visibleColumns = getVisibleColumnsForTab(contactTab, columns);
    // True only when the selected date range actually has activity to show.
    const hasPerfData =
      (totals.sent || 0) > 0 ||
      (totals.opened || 0) > 0 ||
      (totals.clicked || 0) > 0 ||
      (totals.uniqueOpens || 0) > 0 ||
      (totals.uniqueClicks || 0) > 0 ||
      (totals.unsubscribed || 0) > 0 ||
      (totals.bounced || 0) > 0 ||
      visibleRows.length > 0;

    const contactTabsWithCounts = CONTACT_TABS.map((item) => {
      let count = totals.sent || allRows.length;

      if (item.type === "sent") count = totals.sent || allRows.length;
      if (item.type === "open") count = totals.uniqueOpens || totals.opened;
      if (item.type === "click") count = totals.uniqueClicks || totals.clicked;
      if (item.type === "unsubscribe") count = totals.unsubscribed;
      if (item.type === "bounce") count = totals.bounced;

      return {
        ...item,
        count
      };
    });

    return (
      <section className="campaign-detail-page sx-reference-detail">
        {toast && <div className="sendx-toast success">{toast}</div>}

        <div className="campaign-detail-top">
          <div>
            <div className="breadcrumb-line">
              <button onClick={() => { setDetails(null); window.location.hash = "#/Newsletters"; }}>‹ Newsletters</button>
              <span>/</span>
              <strong>{details.name || "Untitled"}</strong>
            </div>
          </div>

          <p className="campaign-time campaign-time-right">
            {statusLabel(details.status)} :{" "}
            {formatDate(campaignWhen(details))}
          </p>
        </div>

        <div className="nl-detail-tabs">
          {DETAIL_TABS.map((item) => (
            <button
              key={item}
              className={detailTab === item ? "active" : ""}
              onClick={() => { setDetailTab(item); syncHashForDetail(details?._id || details?.id, item); }}
            >
              {item}
            </button>
          ))}
        </div>

        {detailTab === "Overview" && (
          <div className="campaign-overview-grid reference-overview">
            <div className="campaign-info-panel">
              <div className="cov-section cov-status-section">
                <h3>Status</h3>
                <span className={`nl-status ${statusLabel(details.status).toLowerCase()}`}>
                  {statusLabel(details.status)}
                </span>
              </div>

              <div className="cov-section">
                <div className="cov-head">
                  <h3>Subject and sender</h3>
                  <button className="cov-edit" onClick={() => { setBuilderCampaign(details); setBuilder(true); }}>Edit subject &amp; sender</button>
                </div>
                <p>Subject: {details.subject || "-"}</p>
                <p>Preview text: {details.previewText || "-"}</p>
                <p className="cov-sender-label">Sender:</p>
                <CampaignSendersList campaign={details} />
              </div>

              <div className="cov-section">
                <div className="cov-head">
                  <h3>Design</h3>
                  <button className="cov-edit" onClick={() => { setBuilderCampaign(details); setBuilder(true); }}>Edit design</button>
                </div>
                <p>Template: {details.template || "-"}</p>
                <p>Email type: {details.editorType || "Drag & Drop"}</p>
              </div>

              <div className="cov-section">
                <div className="cov-head">
                  <h3>Content</h3>
                  <button className="cov-edit" onClick={() => { setBuilderCampaign(details); setBuilder(true); }}>Edit content</button>
                </div>
                <div className="content-test-row">
                  <div className="mini-email-thumb">
                    <EmailPreviewFrame html={details.html} className="email-preview-frame mini" />
                  </div>

                  <button
                    className="ghost-btn"
                    onClick={() => showToast("Test email request prepared.")}
                  >
                    Send Test Email
                  </button>
                </div>
              </div>

              <div className="cov-section">
                <div className="cov-head">
                  <h3>Recipients</h3>
                  <button className="cov-edit" onClick={() => { setBuilderCampaign(details); setBuilder(true); }}>Edit recipients</button>
                </div>
                <p>
                  Send email to:{" "}
                  {details.recipientMode === "all" ? "All contacts" : "Custom segment"}{" "}
                  <span className="segment-pill">segment</span>
                </p>
                <p>Exclude contacts: -</p>
              </div>
            </div>

            <div className="campaign-preview-paper">
              <EmailPreviewFrame html={details.html} className="email-preview-frame large" />
            </div>
          </div>
        )}

        {detailTab === "Performance" && (
          <div className="campaign-performance reference-performance">
            <div className="performance-top-fixed-row">
              <h3>Overall Performance</h3>

              <div className="analytics-date-controls performance-calendar-wrap">
                <div className="analytics-presets">
                  {PERF_PRESETS.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={activePreset === p ? "active" : ""}
                      onClick={() => applyPreset(p)}
                    >
                      {p}
                    </button>
                  ))}
                </div>

                <div className="analytics-date-range">
                  <label>
                    From
                    <input
                      type="date"
                      value={rangeStart}
                      max={rangeEnd}
                      onChange={(e) => { setRangeStart(e.target.value); setActivePreset(""); }}
                    />
                  </label>
                  <label>
                    To
                    <input
                      type="date"
                      value={rangeEnd}
                      min={rangeStart}
                      onChange={(e) => { setRangeEnd(e.target.value); setActivePreset(""); }}
                    />
                  </label>
                </div>
              </div>
            </div>

            <div className="performance-cards">
              <MetricCard title="Sent" value={totals.sent} sub={totals.sent} />
              <MetricCard title="Opened" value={totals.opened} sub={totals.openRate} />
              <MetricCard title="Clicked" value={totals.clicked} sub={totals.clickRate} />
              <MetricCard
                title="Unique Opens"
                value={totals.uniqueOpens}
                sub={totals.uniqueOpenRate}
              />
              <MetricCard
                title="Unique Clicks"
                value={totals.uniqueClicks}
                sub={totals.uniqueClickRate}
              />
            </div>

            <div className="performance-card contacts-performance-card moved-up">
              <div className="contacts-table-toolbar clean">
                <h3>Contacts</h3>

                <div className="contact-actions">
                  <button
                    className="yellow-btn compact export-contacts-btn"
                    onClick={() => exportContacts(visibleRows)}
                  >
                    {contactTab === "sent" ? "Export Sent Contacts" : "Export Contacts"}
                  </button>

                  <button
                    type="button"
                    className="column-options-btn"
                    onClick={() => setColumnModal(true)}
                    aria-label="Choose columns"
                    title="Choose columns"
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M4 7h10M18 7h2M4 17h2M10 17h10"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <circle
                        cx="16"
                        cy="7"
                        r="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                      <circle
                        cx="8"
                        cy="17"
                        r="2"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="contact-tab-row">
                {contactTabsWithCounts.map((item) => (
                  <button
                    key={item.type}
                    className={contactTab === item.type ? "active" : ""}
                    onClick={() => { setContactTab(item.type); syncHashForDetail(details?._id || details?.id, "Performance", item.type); }}
                  >
                    {item.label}
                  </button>
                ))}
              </div>

              {loading && visibleRows.length === 0 ? (
                <div className="no-data-box">Loading...</div>
              ) : visibleRows.length > 0 ? (
                <div className="contacts-scroll-wrap">
                  <button
                    type="button"
                    className="table-scroll-btn left"
                    onClick={() => scrollContacts(-1)}
                    aria-label="Scroll left"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                  <div className="contacts-scroll-area" ref={contactsScrollRef}>
                <table className="contacts-table">
                  <thead>
                    <tr>
                      <th>Sender Name</th>
                      <th>Sender Email</th>
                      {visibleColumns.name && <th>Recipient Name</th>}
                      {visibleColumns.email && <th>Recipient Email</th>}
                      {visibleColumns.status && <th>Status</th>}

                      {visibleColumns.opens && (
                        <th>{contactTab === "all" ? "Unique Opens" : "Opens"}</th>
                      )}

                      {visibleColumns.clicks && (
                        <th>{contactTab === "all" ? "Unique Clicks" : "Clicks"}</th>
                      )}

                      {visibleColumns.sentTime && <th>Sent Time</th>}
                    </tr>
                  </thead>

                  <tbody>
                    {visibleRows.map((row, index) => (
                      <tr key={`${row.email}-${index}`}>
                        <td>{row.senderName || "-"}</td>
                        <td>{row.senderEmail || "-"}</td>

                        {visibleColumns.name && (
                          <td>{row.name || row.email?.split("@")[0] || "Contact"}</td>
                        )}

                        {visibleColumns.email && <td>{row.email}</td>}

                        {visibleColumns.status && (
                          <td>
                            <span
                              className={`sx-status-pill ${row.status}`}
                              title={row.status === "bounced" && row.error ? row.error : undefined}
                            >
                              {row.status}
                            </span>
                            {row.status === "bounced" && row.error && (
                              <div className="sx-bounce-reason" title={row.error}>
                                {row.error}
                              </div>
                            )}
                          </td>
                        )}

                        {visibleColumns.opens && <td>{row.opens || 0}</td>}

                        {visibleColumns.clicks && <td>{row.clicks || 0}</td>}

                        {visibleColumns.sentTime && <td>{formatDate(row.sentTime)}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
                  </div>
                  <button
                    type="button"
                    className="table-scroll-btn right"
                    onClick={() => scrollContacts(1)}
                    aria-label="Scroll right"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  </button>
                </div>
              ) : (
                <div className="no-data-box">No Data</div>
              )}
            </div>
          </div>
        )}

        {detailTab === "Polls" && (
          <div className="polls-page">
            <div className="polls-head">
              <h3>Polls</h3>
              <button className="yellow-btn compact" onClick={createPoll}>
                ＋ Create Poll
              </button>
            </div>

            {polls.length === 0 ? (
              <div className="poll-empty">
                <h3>No polls linked</h3>
                <p>Link a poll to this campaign to see results here.</p>
              </div>
            ) : (
              <div className="poll-list">
                {polls.map((poll) => (
                  <div className="poll-card" key={poll._id}>
                    <h3>{poll.question}</h3>

                    {(poll.options || []).map((option) => (
                      <div className="poll-option" key={option}>
                        <span>{option}</span>
                        <i></i>
                        <b>
                          {(poll.responses || []).filter((r) => r.option === option).length}
                        </b>
                      </div>
                    ))}

                    <p>Responses: {(poll.responses || []).length}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {columnModal && (
          <ColumnModal
            columns={columns}
            setColumns={setColumns}
            contactTab={contactTab}
            onClose={() => setColumnModal(false)}
          />
        )}
      </section>
    );
  }

  return (
    <section className="newsletter-list-page">
      {toast && <div className="sendx-toast success">{toast}</div>}

      <div className="nl-head nl-head-with-date">
        <h2>Newsletters</h2>

        <div className="nl-date-row">
        <div className="analytics-date-controls sx-dashboard-date-wrap">
          <div className="analytics-presets">
            {LIST_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                className={listActivePreset === p ? "active" : ""}
                onClick={() => applyListPreset(p)}
              >
                <span className="nl-label-desktop">{p}</span>
                <span className="nl-label-mobile">{p}</span>
              </button>
            ))}
          </div>

          <div className="analytics-date-range">
            <label>
              From
              <input
                type="date"
                value={listRangeStart}
                max={listRangeEnd}
                onChange={(e) => { setListRangeStart(e.target.value); setListActivePreset(""); }}
              />
            </label>
            <label>
              To
              <input
                type="date"
                value={listRangeEnd}
                min={listRangeStart}
                onChange={(e) => { setListRangeEnd(e.target.value); setListActivePreset(""); }}
              />
            </label>
          </div>
        </div>
        </div>
      </div>

      <div className="nl-tabs">
        {TABS.map((item) => (
          <button
            key={item}
            className={tab === item ? "active" : ""}
            onClick={() => setTab(item)}
          >
            {item}
          </button>
        ))}

        <button className="yellow-btn nl-tabs-create" onClick={() => setBuilder(true)}>
          <span className="nl-create-desktop">＋ Create Newsletter</span>
          <span className="nl-create-mobile">＋ Create</span>
        </button>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-card">
          <h3>No newsletters found</h3>
          <p>Campaigns are safe. Refresh or check backend if this stays empty.</p>
        </div>
      ) : (
        <div className="nl-table-card">
          <div className="nl-table-toolbar">
            <div className="nl-toolbar-left">
              <p>All {filtered.length} newsletters</p>
              {selectedIds.length > 0 && (
                tab === "Archive" ? (
                  <button className="nl-bulk-delete" onClick={deleteSelected}>
                    🗑 Delete ({selectedIds.length})
                  </button>
                ) : (
                  <button className="nl-bulk-archive" onClick={archiveSelected}>
                    🗄 Archive ({selectedIds.length})
                  </button>
                )
              )}
            </div>

            <div>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search newsletters..."
              />

              <button
                className="nl-cols-btn"
                onClick={() => setListColModal(true)}
                title="Choose columns"
              >
                ⚙
              </button>
            </div>
          </div>

          <MobileScrollArea className="nl-table-scroll" ariaLabel="campaign details">
          <table className="nl-table">
            <thead>
              <tr>
                <th className="nl-check-col">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>Name ↕</th>
                {listColumns.sent && <th>Sent ↕</th>}
                {listColumns.opens && <th>Unique Opens ↕</th>}
                {listColumns.opened && <th>% Opened</th>}
                {listColumns.clicks && <th>Unique Clicks ↕</th>}
                {listColumns.clicked && <th>% Clicked</th>}
                {listColumns.created && <th>Created</th>}
                <th></th>
              </tr>
            </thead>

            <tbody>
              {paged.map((campaign) => {
                // "All" => lifetime stats; a specific range => date-scoped totals
                // pulled from the per-campaign performance response (0 when that
                // date has no activity, never the lifetime fallback).
                const dated = listActivePreset !== "All";
                const perf = dated ? listPerfMap[campaign._id] : null;
                let sent, opens, clicks;
                if (dated) {
                  const t = (perf && perf.totals) || {};
                  sent = Number(t.sent || t.sentCount || t.emailsSent || 0);
                  opens = Number(t.uniqueOpens || t.opened || t.opens || 0);
                  clicks = Number(t.uniqueClicks || t.clicked || t.clicks || 0);
                } else {
                  sent = getSentCount(campaign, null);
                  opens = getUniqueOpens(campaign, null);
                  clicks = getUniqueClicks(campaign, null);
                }

                return (
                  <tr key={campaign._id} className="nl-clickable-row" onClick={() => openDetail(campaign)}>
                    <td className="nl-check-col" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(campaign._id)}
                        onChange={() => toggleSelectOne(campaign._id)}
                      />
                    </td>

                    <td className="nl-name-cell">
                      <div className="nl-thumb">
                        <EmailPreviewFrame html={campaign.html} className="email-preview-frame thumb" />
                      </div>

                      <div>
                        <h3>{campaign.name || "Untitled"}</h3>

                        <span className={`nl-status ${statusLabel(campaign.status).toLowerCase()}`}>
                          {statusLabel(campaign.status)}
                        </span>

                        <p>{formatDate(campaignWhen(campaign))}</p>
                      </div>
                    </td>

                    {listColumns.sent && <td>{sent}</td>}
                    {listColumns.opens && <td>{opens}</td>}
                    {listColumns.opened && <td>{sent ? percent(opens, sent) : "NA"}</td>}
                    {listColumns.clicks && <td>{clicks}</td>}
                    {listColumns.clicked && <td>{sent ? percent(clicks, sent) : "NA"}</td>}
                    {listColumns.created && <td>{formatDate(campaign.createdAt)}</td>}

                    <td className={`menu-cell${openMenu === campaign._id ? " menu-open" : ""}`} onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() =>
                          setOpenMenu(openMenu === campaign._id ? null : campaign._id)
                        }
                      >
                        ⋮
                      </button>

                      {openMenu === campaign._id && (
                        <div className="nl-action-menu" onMouseLeave={() => setOpenMenu(null)}>
                          <button onClick={() => openDetail(campaign, "Overview")}>Info</button>
                          <button onClick={() => openDetail(campaign, "Performance")}>View Performance</button>
                          {isSentCampaign(campaign) && <button onClick={() => { setBuilderCampaign(campaign); setBuilder(true); setOpenMenu(null); }}>Resend to Unopeners</button>}

                          {!isSentCampaign(campaign) && (
                            <button
                              onClick={() => {
                                setBuilderCampaign(campaign);
                                setBuilder(true);
                                setOpenMenu(null);
                              }}
                            >
                              Edit
                            </button>
                          )}

                          <button onClick={() => duplicateCampaign(campaign)}>Duplicate</button>
                          <button onClick={() => saveTemplateOnly(campaign)}>Save Template</button>
                          <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/newsletters/${campaign._id}`); showToast("Shareable link copied."); setOpenMenu(null); }}>Shareable link</button>
                          <button onClick={() => archiveCampaign(campaign)}>Archive</button>

                          {!isSentCampaign(campaign) && (
                            <button className="danger" onClick={() => deleteCampaign(campaign)}>Delete</button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </MobileScrollArea>

          <div className="contacts-table-footer">
            <button onClick={exportCampaigns}>⇩ Export</button>
            <div className="nl-pagination">
              <label className="nl-perpage">
                Show
                <select
                  value={perPage}
                  onChange={(e) => {
                    setPerPage(Number(e.target.value));
                    setPageNum(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                per page
              </label>

              <div className="nl-pager">
                <button
                  disabled={safePage <= 1}
                  onClick={() => setPageNum(Math.max(1, safePage - 1))}
                >
                  ‹
                </button>
                <span>Page {safePage} of {totalPages}</span>
                <button
                  disabled={safePage >= totalPages}
                  onClick={() => setPageNum(Math.min(totalPages, safePage + 1))}
                >
                  ›
                </button>
              </div>

              <span className="nl-results-count">
                Showing {filtered.length === 0 ? 0 : (safePage - 1) * perPage + 1} to{" "}
                {Math.min(safePage * perPage, filtered.length)} of {filtered.length} results
              </span>
            </div>
          </div>
        </div>
      )}

      {listColModal && (
        <NlColumnModal
          listColumns={listColumns}
          setListColumns={setListColumns}
          onClose={() => setListColModal(false)}
        />
      )}
    </section>
  );
}

function MetricCard({ title, value, sub }) {
  return (
    <div className="metric-card">
      <span>{title} ⓘ</span>
      <h2>{formatNumber(value || 0)}</h2>
      {sub !== undefined && <p>{sub}</p>}
    </div>
  );
}

function ColumnModal({ columns, setColumns, contactTab, onClose }) {
  const availableColumns = useMemo(() => {
    if (contactTab === "sent") {
      return [
        { key: "name", label: "Name", pinned: true },
        { key: "email", label: "Email" },
        { key: "sentTime", label: "Sent Time" }
      ];
    }

    if (contactTab === "open") {
      return [
        { key: "name", label: "Name", pinned: true },
        { key: "email", label: "Email" },
        { key: "opens", label: "Opens" },
        { key: "sentTime", label: "Sent Time" }
      ];
    }

    if (contactTab === "click") {
      return [
        { key: "name", label: "Name", pinned: true },
        { key: "email", label: "Email" },
        { key: "clicks", label: "Clicks" },
        { key: "sentTime", label: "Sent Time" }
      ];
    }

    if (contactTab === "unsubscribe" || contactTab === "bounce") {
      return [
        { key: "name", label: "Name", pinned: true },
        { key: "email", label: "Email" },
        { key: "status", label: "Status" },
        { key: "sentTime", label: "Sent Time" }
      ];
    }

    return [
      { key: "name", label: "Name", pinned: true },
      { key: "email", label: "Email" },
      { key: "status", label: "Status" },
      { key: "opens", label: "Unique Opens" },
      { key: "clicks", label: "Unique Clicks" },
      { key: "sentTime", label: "Sent Time" }
    ];
  }, [contactTab]);

  const defaultForTab = useMemo(() => {
    const next = {
      name: false,
      email: false,
      status: false,
      sentTime: false,
      opens: false,
      clicks: false
    };

    availableColumns.forEach((column) => {
      next[column.key] = true;
    });

    return next;
  }, [availableColumns]);

  const [local, setLocal] = useState(() => {
    const next = {
      ...defaultForTab,
      ...columns
    };

    availableColumns.forEach((column) => {
      if (column.pinned) {
        next[column.key] = true;
      }
    });

    Object.keys(next).forEach((key) => {
      if (!availableColumns.some((column) => column.key === key)) {
        next[key] = false;
      }
    });

    return next;
  });

  const [columnSearch, setColumnSearch] = useState("");

  const filteredColumns = availableColumns.filter((column) =>
    column.label.toLowerCase().includes(columnSearch.trim().toLowerCase())
  );

  const selectedColumns = availableColumns.filter((column) => local[column.key]);

  function toggleColumn(key) {
    const column = availableColumns.find((item) => item.key === key);

    if (column?.pinned) return;

    setLocal((prev) => ({
      ...prev,
      [key]: !prev[key]
    }));
  }

  function removeColumn(key) {
    const column = availableColumns.find((item) => item.key === key);

    if (column?.pinned) return;

    setLocal((prev) => ({
      ...prev,
      [key]: false
    }));
  }

  function resetColumns() {
    setLocal(defaultForTab);
  }

  function applyColumns() {
    const next = {
      ...columns,
      name: false,
      email: false,
      status: false,
      sentTime: false,
      opens: false,
      clicks: false,
      ...local
    };

    availableColumns.forEach((column) => {
      if (column.pinned) {
        next[column.key] = true;
      }
    });

    setColumns(next);
    onClose();
  }

  return (
    <div className="column-modal-overlay reference-column-overlay">
      <div className="reference-column-modal">
        <div className="reference-column-head">
          <h2>Choose which columns you see</h2>

          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="reference-column-body">
          <div className="reference-column-left">
            <div className="reference-column-search">
              <span>⌕</span>
              <input
                value={columnSearch}
                onChange={(event) => setColumnSearch(event.target.value)}
                placeholder="Search columns..."
              />
            </div>

            <div className="reference-column-list">
              {filteredColumns.map((column) => (
                <label
                  key={column.key}
                  className={column.pinned ? "pinned" : ""}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(local[column.key])}
                    disabled={column.pinned}
                    onChange={() => toggleColumn(column.key)}
                  />

                  <span>{column.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="reference-column-right">
            <p>SELECTED COLUMNS ({selectedColumns.length})</p>

            <div className="selected-column-list">
              {selectedColumns.map((column) => (
                <div
                  className={`selected-column-item ${column.pinned ? "pinned" : ""}`}
                  key={column.key}
                >
                  <span className="selected-column-handle">
                    {column.pinned ? "♙" : "⋮⋮"}
                  </span>

                  <strong>{column.label}</strong>

                  {column.pinned ? (
                    <small>PINNED</small>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeColumn(column.key)}
                      aria-label={`Remove ${column.label}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="reference-column-footer">
          <div>
            <button
              type="button"
              className="reference-apply-btn"
              onClick={applyColumns}
            >
              Apply
            </button>

            <button
              type="button"
              className="reference-cancel-btn"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>

          <button
            type="button"
            className="reference-reset-btn"
            onClick={resetColumns}
          >
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
}

function NlColumnModal({ listColumns, setListColumns, onClose }) {
  const ALL = [
    { key: "name", label: "Name", pinned: true },
    { key: "sent", label: "Sent" },
    { key: "opens", label: "Unique opens" },
    { key: "opened", label: "% Opened" },
    { key: "clicks", label: "Unique clicks" },
    { key: "clicked", label: "% Clicked" },
    { key: "created", label: "Created" }
  ];

  const DEFAULTS = {
    sent: true,
    opens: true,
    opened: true,
    clicks: true,
    clicked: true,
    created: false
  };

  const [local, setLocal] = useState({ ...DEFAULTS, ...listColumns });
  const [columnSearch, setColumnSearch] = useState("");

  const filteredColumns = ALL.filter((column) =>
    column.label.toLowerCase().includes(columnSearch.trim().toLowerCase())
  );

  const selectedColumns = ALL.filter((column) => column.pinned || local[column.key]);

  function toggleColumn(key) {
    const column = ALL.find((item) => item.key === key);
    if (column?.pinned) return;
    setLocal((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function removeColumn(key) {
    const column = ALL.find((item) => item.key === key);
    if (column?.pinned) return;
    setLocal((prev) => ({ ...prev, [key]: false }));
  }

  function resetColumns() {
    setLocal({ ...DEFAULTS });
  }

  function applyColumns() {
    setListColumns({ ...DEFAULTS, ...local });
    onClose();
  }

  return (
    <div className="column-modal-overlay reference-column-overlay">
      <div className="reference-column-modal">
        <div className="reference-column-head">
          <h2>Choose which columns you see</h2>

          <button type="button" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="reference-column-body">
          <div className="reference-column-left">
            <div className="reference-column-search">
              <span>⌕</span>
              <input
                value={columnSearch}
                onChange={(event) => setColumnSearch(event.target.value)}
                placeholder="Search columns..."
              />
            </div>

            <div className="reference-column-list">
              {filteredColumns.map((column) => (
                <label key={column.key} className={column.pinned ? "pinned" : ""}>
                  <input
                    type="checkbox"
                    checked={column.pinned || Boolean(local[column.key])}
                    disabled={column.pinned}
                    onChange={() => toggleColumn(column.key)}
                  />

                  <span>{column.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="reference-column-right">
            <p>SELECTED COLUMNS ({selectedColumns.length})</p>

            <div className="selected-column-list">
              {selectedColumns.map((column) => (
                <div
                  className={`selected-column-item ${column.pinned ? "pinned" : ""}`}
                  key={column.key}
                >
                  <span className="selected-column-handle">
                    {column.pinned ? "♙" : "⋮⋮"}
                  </span>

                  <strong>{column.label}</strong>

                  {column.pinned ? (
                    <small>PINNED</small>
                  ) : (
                    <button
                      type="button"
                      onClick={() => removeColumn(column.key)}
                      aria-label={`Remove ${column.label}`}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="reference-column-footer">
          <div>
            <button type="button" className="reference-apply-btn" onClick={applyColumns}>
              Apply
            </button>

            <button type="button" className="reference-cancel-btn" onClick={onClose}>
              Cancel
            </button>
          </div>

          <button type="button" className="reference-reset-btn" onClick={resetColumns}>
            Reset to defaults
          </button>
        </div>
      </div>
    </div>
  );
}
